/**
 * Stable identifiers for the SacRT bus-stop-shelter asset-management tags.
 *
 * These replace hardcoded numeric tag-definition IDs. A tag definition's numeric ID is assigned
 * per environment, so it cannot be relied on outside the environment it came from; the identifier
 * is slugified from the definition's name and is unique across all definitions, so it is safe to
 * compare directly. The numeric IDs below are recorded only to make the swap auditable.
 */
export const SacRTTag = {
  FadedMissingSign: 'faded_missing_sign', // 'Faded/Missing Sign'
  BrokenBentPole: 'broken_bent_pole', // 'Broken/Bent Pole'
  BrokenGlass: 'broken_glass', // 'Broken Glass'
  Graffiti: 'graffiti', // 'Graffiti'
  LightsOut: 'lights_out', // 'Lights out'
  GarbageOverflow: 'garbage_overflow', // 'Garbage overflow'
  GoodCondition: 'good_condition', // 'Good condition'
  Other: 'other', // 'Other'
  FadedMissingAdvertisements: 'faded_missing_advertisements', // 'Faded/Missing Advertisements'
  UnattendedPersonalProperty: 'unattended_personal_property', // 'Unattended Personal Property'
} as const;

/**
 * Observation tags rendered as icon-only buttons in the asset management view: their names are
 * blanked so the icon carries the meaning.
 */
export const SACRT_ICON_ONLY_TAGS: readonly string[] = Object.values(SacRTTag);

/**
 * Observation tags pre-selected in the SacRT events filter.
 *
 * Each of these definitions owns exactly one active enum variant, whose identifier matches the
 * definition's, so naming the definition is enough to describe a whole filter pair. Good
 * condition is deliberately absent: it is a valid observation but not a default filter.
 */
export const SACRT_DEFAULT_FILTER_TAGS = [
  SacRTTag.Graffiti,
  SacRTTag.FadedMissingSign,
  SacRTTag.FadedMissingAdvertisements,
  SacRTTag.BrokenBentPole,
  SacRTTag.BrokenGlass,
  SacRTTag.LightsOut,
  SacRTTag.GarbageOverflow,
  SacRTTag.Other,
  SacRTTag.UnattendedPersonalProperty,
] as const;

/** Shown as 'Rejection Reason' in the asset management view, rather than its full name. */
export const SACRT_REJECTION_REASON_TAG = 'sacrt_bus_stop_rejection_reason';
